import React, { useEffect, useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { getAccountingDocs, createAccountingDoc, getStudents, getSettings, getCourses, getAccountingDocPdf, getAccountingRevenueAdvanced } from '../services/api';
import { exportTableToPDF } from '../utils/pdfExport';

const docTypeLabels: Record<string, Record<string, string>> = {
  invoice: { he: 'חשבונית מס', ar: 'فاتورة ضريبية' },
  invoice_receipt: { he: 'חשבונית מס קבלה', ar: 'فاتورة ضريبية - إيصال' },
  receipt: { he: 'קבלה', ar: 'إيصال' },
  quote: { he: 'הצעת מחיר', ar: 'عرض سعر' },
  transaction: { he: 'חשבון עסקה', ar: 'حساب صفقة' },
};

const VAT_RATE = 0.17;

export default function Accounting() {
  const { t, lang } = useLang();
  const { userName } = useUser();
  const slang = lang === 'ar' ? 'ar' : 'he';
  const [docs, setDocs] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [settings, setSettingsData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('documents');
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDocType, setFilterDocType] = useState('');
  const [revenueData, setRevenueData] = useState<any>(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [groupBy, setGroupBy] = useState('month');
  const [advReport, setAdvReport] = useState<any>(null);

  const [form, setForm] = useState({
    docType: 'invoice',
    date: new Date().toISOString().split('T')[0],
    customerName: '',
    customerAddress: '',
    customerPhone: '',
    customerEmail: '',
    customerIdNumber: '',
    studentId: '',
    courseId: '',
    semester: '',
    items: [{ description: '', quantity: 1, unitPrice: 0 }] as { description: string; quantity: number; unitPrice: number }[],
    notes: '',
    performedBy: ''
  });

  const loadData = () => {
    getAccountingDocs({ docType: filterDocType || undefined, search: searchTerm || undefined })
      .then(res => setDocs(res.data)).catch(() => {});
    getStudents().then(res => setStudents(res.data)).catch(() => {});
    getCourses().then(res => setCourses(res.data)).catch(() => {});
    getSettings().then(res => setSettingsData(res.data)).catch(() => {});
  };

  useEffect(() => { loadData(); }, [filterDocType, searchTerm]);

  useEffect(() => {
    if (activeTab === 'revenue') {
      getAccountingRevenueAdvanced({ groupBy, year: selectedYear })
        .then(res => setAdvReport(res.data)).catch(() => {});
    }
  }, [activeTab, groupBy, selectedYear]);

  const calcTotals = () => {
    const beforeVat = form.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
    const vat = Math.round(beforeVat * VAT_RATE);
    const withVat = beforeVat + vat;
    return { beforeVat, vat, withVat };
  };

  const handleAddItem = () => {
    setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unitPrice: 0 }] });
  };

  const handleRemoveItem = (index: number) => {
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    const items = [...form.items];
    (items[index] as any)[field] = value;
    setForm({ ...form, items });
  };

  const handleStudentSelect = (studentId: string) => {
    const student = students.find(s => s.id === studentId);
    if (student) {
      setForm({
        ...form,
        studentId,
        customerName: `${student.firstName} ${student.lastName}`,
        customerEmail: student.email || '',
        customerPhone: student.phone || '',
        customerAddress: student.address || '',
        customerIdNumber: student.nationalId || '',
      });
    }
  };

  const handleCreate = async () => {
    const totals = calcTotals();
    await createAccountingDoc({
      ...form,
      totalBeforeVat: totals.beforeVat,
      vatAmount: totals.vat,
      totalWithVat: totals.withVat,
    });
    setShowModal(false);
    setForm({
      docType: 'invoice', date: new Date().toISOString().split('T')[0],
      customerName: '', customerAddress: '', customerPhone: '', customerEmail: '',
      customerIdNumber: '', studentId: '', courseId: '', semester: '',
      items: [{ description: '', quantity: 1, unitPrice: 0 }],
      notes: '', performedBy: userName
    });
    loadData();
  };

  const exportDocToPDF = async (doc: any) => {
    try {
      const res = await getAccountingDocPdf(doc.id);
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (e) { alert('PDF error'); }
  };

  const formatPhoneForWhatsApp = (phone: string) => {
    const cleaned = phone.replace(/[^0-9]/g, '');
    return cleaned.startsWith('0') ? '972' + cleaned.slice(1) : '972' + cleaned;
  };

  const totals = calcTotals();

  // Revenue report calculation
  const filteredForRevenue = docs.filter(d => ['invoice', 'invoice_receipt', 'receipt'].includes(d.docType));
  const yearDocs = filteredForRevenue.filter(d => {
    const date = new Date(d.date || d.createdAt);
    return date.getFullYear() === parseInt(selectedYear);
  });
  const monthlyData: Record<string, { total: number; count: number }> = {};
  yearDocs.forEach(d => {
    const date = new Date(d.date || d.createdAt);
    const m = date.getMonth() + 1;
    const key = String(m).padStart(2, '0');
    if (!monthlyData[key]) monthlyData[key] = { total: 0, count: 0 };
    monthlyData[key].total += (d.totalWithVat || 0);
    monthlyData[key].count += 1;
  });
  const yearTotal = yearDocs.reduce((sum, d) => sum + (d.totalWithVat || 0), 0);

  return (
    <div>
      <div className="page-title">
        <span className="page-title-text">📑 {t('accounting')}</span>
        <button className="btn btn-primary" onClick={() => { setForm({...form, performedBy: userName}); setShowModal(true); }}>+ {t('newDocument')}</button>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'documents' ? 'active' : ''}`} onClick={() => setActiveTab('documents')}>{t('allDocuments')}</button>
        <button className={`tab ${activeTab === 'revenue' ? 'active' : ''}`} onClick={() => setActiveTab('revenue')}>{t('revenueReportMonthly')}</button>
      </div>

      {activeTab === 'documents' && (
        <div className="page-section">
          <div className="toolbar" style={{ marginBottom: 16 }}>
            <input className="form-input" style={{ maxWidth: 250 }} placeholder={t('search')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            <select className="form-select" style={{ width: 'auto', minWidth: 150 }} value={filterDocType} onChange={e => setFilterDocType(e.target.value)}>
              <option value="">{t('all')}</option>
              <option value="invoice">{docTypeLabels.invoice[slang]}</option>
              <option value="invoice_receipt">{docTypeLabels.invoice_receipt[slang]}</option>
              <option value="receipt">{docTypeLabels.receipt[slang]}</option>
              <option value="quote">{docTypeLabels.quote[slang]}</option>
              <option value="transaction">{docTypeLabels.transaction[slang]}</option>
            </select>
          </div>

          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('docNumber')}</th>
                <th>{t('docType')}</th>
                <th>{t('date')}</th>
                <th>{t('customerName')}</th>
                <th>{t('totalBeforeVat')}</th>
                <th>{t('vat')}</th>
                <th>{t('totalWithVat')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {docs.map(doc => (
                <tr key={doc.id}>
                  <td style={{ fontWeight: 600 }}>{doc.docNumber}</td>
                  <td><span className={`badge ${
                    doc.docType === 'invoice' ? 'badge-blue' :
                    doc.docType === 'invoice_receipt' ? 'badge-green' :
                    doc.docType === 'receipt' ? 'badge-gold' :
                    doc.docType === 'quote' ? 'badge-yellow' : 'badge-gray'
                  }`}>{docTypeLabels[doc.docType]?.[slang] || doc.docType}</span></td>
                  <td>{doc.date}</td>
                  <td>{doc.customerName}</td>
                  <td>{(doc.totalBeforeVat || 0).toLocaleString()} ₪</td>
                  <td>{(doc.vatAmount || 0).toLocaleString()} ₪</td>
                  <td style={{ fontWeight: 600 }}>{(doc.totalWithVat || 0).toLocaleString()} ₪</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <button className="btn btn-sm" style={{ background: '#B71C1C', color: '#fff', border: 'none' }} onClick={() => exportDocToPDF(doc)}>📄 PDF</button>
                      {doc.customerEmail && (
                        <button className="btn btn-sm btn-email" onClick={() => {
                          window.open(`mailto:${doc.customerEmail}?subject=${docTypeLabels[doc.docType]?.[slang]} ${doc.docNumber}`, '_blank');
                        }}>📧</button>
                      )}
                      {doc.customerPhone && (
                        <button className="btn btn-sm btn-whatsapp" onClick={() => {
                          const waPhone = formatPhoneForWhatsApp(doc.customerPhone);
                          const text = encodeURIComponent(`שלום ${doc.customerName}, מצורפ/ת ${docTypeLabels[doc.docType]?.[slang]} מספר ${doc.docNumber} בסך ${(doc.totalWithVat || 0).toLocaleString()} ₪`);
                          window.open(`https://wa.me/${waPhone}?text=${text}`, '_blank');
                        }}>📱</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {docs.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', color: '#999' }}>{t('noData')}</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {activeTab === 'revenue' && (
        <div className="page-section">
          <div className="toolbar" style={{ marginBottom: 16 }}>
            <select className="form-select" style={{ width: 'auto' }} value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select className="form-select" style={{ width: 'auto' }} value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              <option value="month">{t('byMonth')}</option>
              <option value="semester">{t('bySemester')}</option>
              <option value="course">{t('byCourse')}</option>
            </select>
          </div>

          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: 24 }}>
            <div className="stat-card">
              <div className="icon">💰</div>
              <div className="value">{yearTotal.toLocaleString()} ₪</div>
              <div className="label">{t('totalRevenue')} {selectedYear}</div>
            </div>
            <div className="stat-card" style={{ borderRightColor: '#81D4FA' }}>
              <div className="icon">📊</div>
              <div className="value">{yearDocs.length}</div>
              <div className="label">{t('count')}</div>
            </div>
          </div>

          <h4 style={{ color: '#7B1FA2', marginBottom: 12 }}>{t('revenueReportMonthly')}</h4>
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('month')}</th>
                <th>{t('count')}</th>
                <th>{t('total')}</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 12 }, (_, i) => {
                const key = String(i + 1).padStart(2, '0');
                const data = monthlyData[key] || { total: 0, count: 0 };
                const monthNames = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
                return (
                  <tr key={key}>
                    <td>{monthNames[i]}</td>
                    <td>{data.count}</td>
                    <td style={{ fontWeight: data.total > 0 ? 600 : 400 }}>{data.total.toLocaleString()} ₪</td>
                  </tr>
                );
              })}
              <tr style={{ background: '#f5f5f5', fontWeight: 700 }}>
                <td>{t('total')}</td>
                <td>{yearDocs.length}</td>
                <td>{yearTotal.toLocaleString()} ₪</td>
              </tr>
            </tbody>
          </table>
          </div>

          {advReport && (
            <div style={{ marginTop: 24 }}>
              <h4 style={{ color: '#7B1FA2', marginBottom: 12 }}>{t('revenueBy')}</h4>
              <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{groupBy === 'month' ? t('month') : groupBy === 'semester' ? t('semester') : t('courses')}</th>
                    <th>{t('count')}</th>
                    <th>{t('total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(advReport.rows || []).map((row: any) => (
                    <tr key={row.key}>
                      <td>{row.label}</td>
                      <td>{row.count}</td>
                      <td style={{ fontWeight: (row.total || 0) > 0 ? 600 : 400 }}>{(row.total || 0).toLocaleString()} ₪</td>
                    </tr>
                  ))}
                  {(advReport.rows || []).length === 0 && (
                    <tr><td colSpan={3} style={{ textAlign: 'center', color: '#999' }}>{t('noData')}</td></tr>
                  )}
                  <tr style={{ background: '#f5f5f5', fontWeight: 700 }}>
                    <td>{t('total')}</td>
                    <td>{advReport.count}</td>
                    <td>{(advReport.total || 0).toLocaleString()} ₪</td>
                  </tr>
                </tbody>
              </table>
              </div>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <button className="btn" style={{ background: '#B71C1C', color: '#fff', border: 'none' }}
              onClick={() => {
                const headers = [t('month'), t('count'), t('total')];
                const monthNames = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
                const rows = Array.from({ length: 12 }, (_, i) => {
                  const key = String(i + 1).padStart(2, '0');
                  const data = monthlyData[key] || { total: 0, count: 0 };
                  return [monthNames[i], String(data.count), `${data.total.toLocaleString()} NIS`];
                });
                exportTableToPDF(`${t('revenueReportMonthly')} ${selectedYear}`, headers, rows, `revenue_${selectedYear}.pdf`);
              }}
            >📄 {t('exportPDF')}</button>
          </div>
        </div>
      )}

      {/* Create Document Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div className="modal-header">
              <h3 className="modal-title">📑 {t('newDocument')}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('docType')}</label>
                  <select className="form-select" value={form.docType} onChange={e => setForm({...form, docType: e.target.value})}>
                    <option value="invoice">{docTypeLabels.invoice[slang]}</option>
                    <option value="invoice_receipt">{docTypeLabels.invoice_receipt[slang]}</option>
                    <option value="receipt">{docTypeLabels.receipt[slang]}</option>
                    <option value="quote">{docTypeLabels.quote[slang]}</option>
                    <option value="transaction">{docTypeLabels.transaction[slang]}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{t('date')}</label>
                  <input className="form-input" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">{t('students')} ({lang === 'ar' ? 'اختياري' : 'אופציונלי'})</label>
                <select className="form-select" value={form.studentId} onChange={e => handleStudentSelect(e.target.value)}>
                  <option value="">-- בחר סטודנט --</option>
                  {students.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('linkToCourse')} ({lang === 'ar' ? 'اختياري' : 'אופציונלי'})</label>
                  <select className="form-select" value={form.courseId} onChange={e => setForm({...form, courseId: e.target.value})}>
                    <option value="">-- {t('courses')} --</option>
                    {courses.map(c => <option key={c.id} value={c.id}>{lang === 'ar' && c.nameAr ? c.nameAr : c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{t('semester')} ({lang === 'ar' ? 'اختياري' : 'אופציונלי'})</label>
                  <select className="form-select" value={form.semester} onChange={e => setForm({...form, semester: e.target.value})}>
                    <option value="">--</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="summer">{lang === 'ar' ? 'صيفي' : 'קיץ'}</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('customerName')} *</label>
                  <input className="form-input" value={form.customerName} onChange={e => setForm({...form, customerName: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('customerIdNumber')}</label>
                  <input className="form-input" value={form.customerIdNumber} onChange={e => setForm({...form, customerIdNumber: e.target.value})} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('customerAddress')}</label>
                  <input className="form-input" value={form.customerAddress} onChange={e => setForm({...form, customerAddress: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('customerPhone')}</label>
                  <input className="form-input" type="tel" value={form.customerPhone} onChange={e => setForm({...form, customerPhone: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('customerEmail')}</label>
                  <input className="form-input" type="email" value={form.customerEmail} onChange={e => setForm({...form, customerEmail: e.target.value})} />
                </div>
              </div>

              <h4 style={{ color: '#7B1FA2', margin: '16px 0 12px' }}>{t('lineItems')}</h4>
              {form.items.map((item, i) => (
                <div key={i} className="form-row" style={{ alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ flex: 3 }}>
                    <label className="form-label">{t('itemDescription')}</label>
                    <input className="form-input" value={item.description} onChange={e => handleItemChange(i, 'description', e.target.value)} />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">{t('quantity')}</label>
                    <input className="form-input" type="number" min="1" value={item.quantity} onChange={e => handleItemChange(i, 'quantity', parseInt(e.target.value) || 1)} />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">{t('unitPrice')} (₪)</label>
                    <input className="form-input" type="number" value={item.unitPrice} onChange={e => handleItemChange(i, 'unitPrice', parseInt(e.target.value) || 0)} />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">{t('lineTotal')}</label>
                    <div className="form-input" style={{ background: '#f8f9fa', fontWeight: 600 }}>{(item.quantity * item.unitPrice).toLocaleString()} ₪</div>
                  </div>
                  {form.items.length > 1 && (
                    <button className="btn btn-sm btn-danger" style={{ marginBottom: 16 }} onClick={() => handleRemoveItem(i)}>✕</button>
                  )}
                </div>
              ))}
              <button className="btn btn-sm btn-outline" onClick={handleAddItem} style={{ marginBottom: 16 }}>+ {t('addLine')}</button>

              <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>{t('totalBeforeVat')}:</span>
                  <span style={{ fontWeight: 600 }}>{totals.beforeVat.toLocaleString()} ₪</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>{t('vat')}:</span>
                  <span>{totals.vat.toLocaleString()} ₪</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 18, color: '#7B1FA2' }}>
                  <span>{t('totalWithVat')}:</span>
                  <span>{totals.withVat.toLocaleString()} ₪</span>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: 16 }}>
                <label className="form-label">{t('notes')}</label>
                <textarea className="form-textarea" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleCreate} disabled={!form.customerName || form.items[0].unitPrice === 0}>
                📑 {t('save')}
              </button>
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
